import { css } from '@emotion/react';
import { Modal, ModalDialog, styled } from '@mui/joy';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import z from 'zod';

import { BackendReportContent } from '@/components/backend/report/BackendReportContent';
import { useReportId } from '@/hooks/useReportId';
import { makeMetaTitle } from '@/modules/META';
import { backendApiReportsFindOneOptions } from '@/modules/api/@tanstack/react-query.gen';

const zValidateSearch = z.object({
  zustaendigkeit: z.boolean().optional().catch(undefined),
  status: z.boolean().optional().catch(undefined),
  sichtbarkeit: z.boolean().optional().catch(undefined),
  kommentar: z.boolean().optional().catch(undefined),
  weiterleiten: z.boolean().optional().catch(undefined),

  email: z.string().optional().catch(undefined),
  weitergeleitet: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/verwaltung/{-$adminLevelId}/meldungen/$reportId')({
  validateSearch: zValidateSearch,

  loader: async ({ params: { reportId }, context: { queryClient } }) => ({
    report: await queryClient.ensureQueryData(backendApiReportsFindOneOptions({ path: { reportId } })),
  }),

  head: ({ loaderData }) => ({
    meta: loaderData?.report ? [makeMetaTitle([`Meldung ${loaderData?.report.sequentialId}`])] : undefined,
  }),

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

const StyledModalDialog = styled(ModalDialog)(
  ({ theme }) => css`
    opacity: 0;
    top: ${theme.spacing(10)};
    right: 0;
    bottom: ${theme.spacing(7)};
    left: auto;
    border-radius: 0;
    padding: 0;
    transform: translateX(50%);
    transition:
      opacity 300ms,
      transform 300ms;
  `,
);

function RouteComponent() {
  const nodeRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { reportId } = useReportId();
  const search = Route.useSearch();

  const form =
    Array.from(Object.keys(search)).filter((item) =>
      ['weiterleiten', 'zustaendigkeit', 'status', 'sichtbarkeit', 'kommentar'].includes(item),
    ).length > 0;

  const [modalIn, setModalIn] = useState(false);
  const [formIn, setFormIn] = useState(false);

  useEffect(() => {
    setModalIn(true);
  }, []);

  useEffect(() => {
    setFormIn(form);
  }, [form]);

  return (
    <Transition
      key={reportId}
      appear
      in={modalIn}
      nodeRef={nodeRef}
      onExited={async () => navigate({ to: '..' })}
      timeout={300}
    >
      {(modalTransitionState) => (
        <Transition appear in={formIn} nodeRef={nodeRef} timeout={600}>
          {(formTransitionState) => {
            const hideBackdrop = !form && formTransitionState === 'exited';

            return (
              <Modal
                ref={nodeRef}
                keepMounted
                open
                disableEnforceFocus={hideBackdrop}
                hideBackdrop={hideBackdrop}
                onClose={() => setModalIn(false)}
                slotProps={{
                  backdrop: {
                    sx: {
                      opacity: 0,
                      transition: 'opacity 600ms ease',
                      ...(['entering', 'entered'].includes(formTransitionState) && {
                        opacity: 1,
                      }),
                    },
                  },
                }}
                sx={{
                  visibility: modalTransitionState === 'exited' ? 'hidden' : 'visible',
                  ...(!formIn && { position: 'absolute', top: 0, right: 0, bottom: 0, left: 'auto' }),
                }}
              >
                <StyledModalDialog
                  maxWidth="none"
                  sx={(theme) => ({
                    ...(['entering', 'entered'].includes(modalTransitionState) && {
                      opacity: 1,
                      transform: 'none',
                    }),
                    ...(['entering', 'entered'].includes(formTransitionState) && {
                      transform: 'none',
                      borderTopLeftRadius: theme.radius.xl,
                      borderBottomLeftRadius: theme.radius.xl,
                    }),
                  })}
                >
                  <BackendReportContent onClose={() => setModalIn(false)} />
                </StyledModalDialog>
              </Modal>
            );
          }}
        </Transition>
      )}
    </Transition>
  );
}
